import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Grid,
  Box,
  Heading,
  Flex,
  Spinner,
  Text,
  Button
} from '@chakra-ui/react'
import { FC, useCallback, useEffect, useMemo, useState } from 'react'
import Chart from 'react-apexcharts'
import { useImmer } from 'use-immer'

import generic from '@service/generic'

import {
  IGraphicResponse,
  IGraphicValues
} from '@base/@types/providers/Mrp/PurchaseProvider'

import {
  dailyChart,
  dailySeries,
  montlyChart,
  montlySeries
} from './ParseInfos/parsePeriodChart'

interface MrpPeriodChartProps {
  title: string
  list: any[]
  selected?: any
  setSelected: (value: any) => void
}

enum DaysQuantity {
  month = 30,
  sixMonths = 180,
  year = 365
}

const getDaysQuantity = (timeline: string) => {
  switch (timeline) {
    case 'one_month':
      return DaysQuantity.month
    case 'six_months':
      return DaysQuantity.sixMonths
    case 'one_years':
      return DaysQuantity.year
    default:
      return null
  }
}

const ReabPeriodChart: FC<MrpPeriodChartProps> = ({
  title,
  list,
  selected,
  setSelected
}) => {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(false)
  const [selection, setSelection] = useState('one_month')

  const [data, updateData] = useImmer<IGraphicResponse>({
    storage: [],
    sales: []
  })

  const [filteredData, setFilteredData] = useImmer<IGraphicResponse>({
    storage: [],
    sales: []
  })

  const item = useMemo(() => {
    if (selected) {
      const item = list.find(
        (e: any) => e.produtoCodigo === selected['produto-id']
      )
      return item
    }

    return null
  }, [list, selected])

  const handleModalClose = () => {
    setSelected(null)
  }

  const updateDataGraph = (timeline: string) => {
    setSelection(timeline)

    const daysQuantity = getDaysQuantity(timeline)

    const dateNow = new Date()
    const oldDate = new Date().setDate(dateNow.getDate() - daysQuantity)
    const currentDate = new Date().setDate(dateNow.getDate())

    const chartData = {
      storage: data.storage.filter(item => {
        const itemDate = new Date(item.data)

        return itemDate > new Date(oldDate) && itemDate <= new Date(currentDate)
      }),
      sales: data.sales.filter(item => {
        const itemDate = new Date(item.data)

        return itemDate > new Date(oldDate) && itemDate <= new Date(currentDate)
      })
    }

    setFilteredData(timeline === 'all' ? data : chartData)
  }

  const getChartData = useCallback(async () => {
    try {
      setLoading(true)
      setError(false)

      const {
        data: { data: storage }
      } = await generic.getStorageChart(
        selected['centro-id'],
        selected['produto-id']
      )

      const {
        data: { data: sales }
      } = await generic.getSalesChart(
        selected['centro-id'],
        selected['produto-id']
      )

      updateData(draft => {
        draft.storage = storage
        draft.sales = sales
      })

      setFilteredData(() => {
        setSelection('one_month')

        const daysQuantity = getDaysQuantity('one_month')

        const dateNow = new Date()
        const oldDate = new Date().setDate(dateNow.getDate() - daysQuantity)
        const currentDate = new Date().setDate(dateNow.getDate())

        return {
          storage: storage.filter((item: IGraphicValues) => {
            const itemDate = new Date(item.data)

            return (
              itemDate > new Date(oldDate) && itemDate <= new Date(currentDate)
            )
          }),
          sales: sales.filter((item: IGraphicValues) => {
            const itemDate = new Date(item.data)

            return (
              itemDate > new Date(oldDate) && itemDate <= new Date(currentDate)
            )
          })
        }
      })

      setLoading(false)
    } catch {
      setError(true)
      setLoading(false)
    }
  }, [selected, setFilteredData, updateData])

  useEffect(() => {
    if (selected) {
      getChartData()
    }
  }, [selected, getChartData])

  return (
    <Modal
      isOpen={!!selected}
      onClose={handleModalClose}
      motionPreset="slideInBottom"
      size="4xl"
    >
      <ModalOverlay />
      <ModalContent>
        <ModalHeader borderTopRadius={5} bgColor="white">
          Gráficos
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody borderBottomRadius={5} bgColor="white" pb={6}>
          <Tabs colorScheme="green" variant="soft-rounded" isFitted>
            <TabList>
              <Tab _focus={{ boxShadow: 'none' }}>Diário</Tab>
              <Tab _focus={{ boxShadow: 'none' }}>Mensal</Tab>
            </TabList>

            {loading ? (
              <Flex alignItems="center" justifyContent="center" h={240}>
                <Spinner />
              </Flex>
            ) : error ? (
              <Flex alignItems="center" justifyContent="center" h="120px">
                <Text>Dados insuficientes para o gráfico</Text>
              </Flex>
            ) : (
              <TabPanels>
                <TabPanel>
                  <Flex marginBottom={2}>
                    <Button
                      id="one_month"
                      marginRight={2}
                      fontSize={14}
                      size="sm"
                      onClick={() => updateDataGraph('one_month')}
                      borderColor="green"
                      backgroundColor={
                        selection === 'one_month' ? 'green.100' : 'gray.100'
                      }
                    >
                      1M
                    </Button>

                    <Button
                      id="six_months"
                      marginRight={2}
                      fontSize={14}
                      size="sm"
                      transition="all ease .3s"
                      onClick={() => updateDataGraph('six_months')}
                      backgroundColor={
                        selection === 'six_months' ? 'green.100' : 'gray.100'
                      }
                    >
                      6M
                    </Button>

                    <Button
                      id="one_month"
                      marginRight={2}
                      fontSize={14}
                      size="sm"
                      transition="all ease .3s"
                      onClick={() => updateDataGraph('one_years')}
                      backgroundColor={
                        selection === 'one_years' ? 'green.100' : 'gray.100'
                      }
                    >
                      1Y
                    </Button>

                    <Button
                      id="all_months"
                      marginRight={2}
                      fontSize={14}
                      size="sm"
                      transition="all ease .3s"
                      onClick={() => updateDataGraph('all')}
                      backgroundColor={
                        selection === 'all' ? 'green.100' : 'gray.100'
                      }
                    >
                      ALL
                    </Button>
                  </Flex>

                  <Chart
                    options={dailyChart(filteredData, title)}
                    series={dailySeries(filteredData)}
                    width="100%"
                    height="240px"
                  />
                </TabPanel>

                <TabPanel>
                  <Chart
                    options={montlyChart(data, title)}
                    series={montlySeries(data)}
                    width="100%"
                    height="240px"
                  />
                </TabPanel>
              </TabPanels>
            )}
          </Tabs>

          <Box mb={4}>
            <Heading size="md" mb={2}>
              Estoque
            </Heading>
            <Grid templateColumns="repeat(3, 1fr)" gap="24px">
              <Box p={4} bg="gray.100" borderRadius="lg">
                <Heading
                  size="xs"
                  fontWeight="light"
                  mb={1}
                  maxW="100%"
                  isTruncated
                >
                  Reservado
                </Heading>
                <Text fontSize="2xl" lineHeight="1.2">
                  {item ? item.grafico.estoqueReservado : '-/-'}
                </Text>
              </Box>
              <Box p={4} bg="gray.100" borderRadius="lg">
                <Heading
                  size="xs"
                  fontWeight="light"
                  mb={1}
                  maxW="100%"
                  isTruncated
                >
                  Dep. 13
                </Heading>
                <Text fontSize="2xl" lineHeight="1.2">
                  {item ? item.grafico.estoqueDep13 : '-/-'}
                </Text>
              </Box>
              <Box p={4} bg="gray.100" borderRadius="lg">
                <Heading
                  size="xs"
                  fontWeight="light"
                  mb={1}
                  maxW="100%"
                  isTruncated
                >
                  Dep. 16
                </Heading>
                <Text fontSize="2xl" lineHeight="1.2">
                  {item ? item.grafico.estoqueDep16 : '-/-'}
                </Text>
              </Box>
            </Grid>
          </Box>

          <Box mb={4}>
            <Heading size="md" mb={2}>
              Parâmetros
            </Heading>
            <Grid templateColumns="repeat(4, 1fr)" gap="24px">
              <Box p={4} bg="gray.100" borderRadius="lg">
                <Heading
                  size="xs"
                  fontWeight="light"
                  mb={1}
                  maxW="100%"
                  isTruncated
                >
                  Frequência
                </Heading>
                <Text fontSize="2xl" lineHeight="1.2">
                  {item ? item.grafico.parametroFrequencia : '-/-'}
                </Text>
              </Box>
              <Box p={4} bg="gray.100" borderRadius="lg">
                <Heading
                  size="xs"
                  fontWeight="light"
                  mb={1}
                  maxW="100%"
                  isTruncated
                >
                  Lead Time
                </Heading>
                <Text fontSize="2xl" lineHeight="1.2">
                  {item ? item.grafico.parametroLeadTime : '-/-'}
                </Text>
              </Box>
              <Box p={4} bg="gray.100" borderRadius="lg">
                <Heading
                  size="xs"
                  fontWeight="light"
                  mb={1}
                  maxW="100%"
                  isTruncated
                >
                  Estq. Seg.
                </Heading>
                <Text fontSize="2xl" lineHeight="1.2">
                  {item ? item.grafico.parametroEstoqueSeg : '-/-'}
                </Text>
              </Box>
              <Box p={4} bg="gray.100" borderRadius="lg">
                <Heading
                  size="xs"
                  fontWeight="light"
                  mb={1}
                  maxW="100%"
                  isTruncated
                >
                  Lote Mult.
                </Heading>
                <Text fontSize="2xl" lineHeight="1.2">
                  {item ? item.grafico.parametroLoteMultiplo : ''}
                </Text>
              </Box>
            </Grid>
          </Box>

          <Box mb={4}>
            <Heading size="md" mb={2}>
              Previsões
            </Heading>
            <Grid templateColumns="repeat(4, 1fr)" gap="24px">
              <Box p={4} bg="gray.100" borderRadius="lg">
                <Heading
                  size="xs"
                  fontWeight="light"
                  mb={1}
                  maxW="100%"
                  isTruncated
                >
                  Período
                </Heading>
                <Text fontSize="2xl" lineHeight="1.2">
                  {item ? item.grafico.previsaoPeriodo : '-/-'}
                </Text>
              </Box>
              <Box p={4} bg="gray.100" borderRadius="lg">
                <Heading
                  size="xs"
                  fontWeight="light"
                  mb={1}
                  maxW="100%"
                  isTruncated
                >
                  Venda Projetada
                </Heading>
                <Text fontSize="2xl" lineHeight="1.2">
                  {item
                    ? item.grafico.previsaoVendaProjetada.toFixed(2)
                    : '-/-'}
                </Text>
              </Box>
              <Box p={4} bg="gray.100" borderRadius="lg">
                <Heading
                  size="xs"
                  fontWeight="light"
                  mb={1}
                  maxW="100%"
                  isTruncated
                >
                  Envios Projetados
                </Heading>
                <Text fontSize="2xl" lineHeight="1.2">
                  {item
                    ? item.grafico.previsaoEnvioProjetado
                      ? item.grafico.previsaoEnvioProjetado
                      : '-/-'
                    : '-/-'}
                </Text>
              </Box>
              <Box p={4} bg="gray.100" borderRadius="lg">
                <Heading
                  size="xs"
                  fontWeight="light"
                  mb={1}
                  maxW="100%"
                  isTruncated
                >
                  Estq. Seg.
                </Heading>
                <Text fontSize="2xl" lineHeight="1.2">
                  {item
                    ? item.grafico.previsaoEstoqueSeguranca.toFixed(2)
                    : '-/-'}
                </Text>
              </Box>
            </Grid>
          </Box>

          <Box>
            <Heading size="md" mb={2}>
              Outros
            </Heading>
            <Grid templateColumns="repeat(4, 1fr)" gap="24px">
              <Box p={4} bg="gray.100" borderRadius="lg">
                <Heading
                  size="xs"
                  fontWeight="light"
                  mb={1}
                  maxW="100%"
                  isTruncated
                >
                  Leo
                </Heading>
                <Text fontSize="2xl" lineHeight="1.2">
                  {item ? item.grafico.outrosLeo || '-/-' : '-/-'}
                </Text>
              </Box>
              <Box p={4} bg="gray.100" borderRadius="lg">
                <Heading
                  size="xs"
                  fontWeight="light"
                  mb={1}
                  maxW="100%"
                  isTruncated
                >
                  Fornecedor
                </Heading>
                <Text fontSize="2xl" maxW="160px" isTruncated lineHeight="1.2">
                  {item
                    ? item.grafico.outrosFornecedor
                      ? item.grafico.outrosFornecedor
                      : '-/-'
                    : '-/-'}
                </Text>
              </Box>
              <Box p={4} bg="gray.100" borderRadius="lg">
                <Heading
                  size="xs"
                  fontWeight="light"
                  mb={1}
                  maxW="100%"
                  isTruncated
                >
                  Fator Conv.
                </Heading>
                <Text fontSize="2xl" lineHeight="1.2">
                  {item
                    ? item.grafico.outrosFatorConversao
                      ? item.grafico.outrosFatorConversao
                      : '-/-'
                    : '-/-'}
                </Text>
              </Box>
              <Box p={4} bg="gray.100" borderRadius="lg">
                <Heading
                  size="xs"
                  fontWeight="light"
                  mb={1}
                  maxW="100%"
                  isTruncated
                >
                  Custo Unit.
                </Heading>
                <Text fontSize="2xl" lineHeight="1.2">
                  {item ? item.grafico.outrosCustoUnitario : '-/-'}
                </Text>
              </Box>
            </Grid>
          </Box>
        </ModalBody>
      </ModalContent>
    </Modal>
  )
}

export default ReabPeriodChart
